import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  DeploymentSnapshot,
  FleetAnchorsResponse,
  MakeShellApi,
  MfeDetailResponse,
  NegotiationSimulateRequest,
  OverviewResponse,
  ReadinessResponse,
  SimulationResponse,
} from './types';

/**
 * Talks to this plugin's own backend, which holds the diagnostics bearer token
 * and fans out to the make-shell deployments server-side. The browser never
 * reaches a shell-server directly — there are no CORS headers on
 * `/api/shell/*`, and the deployment inventory is backend config.
 */
export class MakeShellClient implements MakeShellApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('make-shell');
  }

  async getOverview(): Promise<OverviewResponse> {
    return this.request('/deployments');
  }

  async refresh(): Promise<OverviewResponse> {
    return this.request('/refresh', { method: 'POST' });
  }

  async getMfeDetail(
    deploymentId: string,
    mfeName: string,
  ): Promise<MfeDetailResponse> {
    // Both segments reach here from a URL, and a remote name is free-form in
    // make-shell's config, so neither is safe to interpolate raw.
    return this.request(
      `/deployments/${encodeURIComponent(
        deploymentId,
      )}/mfe/${encodeURIComponent(mfeName)}`,
    );
  }

  async simulate(
    deploymentId: string,
    body: NegotiationSimulateRequest,
  ): Promise<SimulationResponse> {
    return this.request(
      `/deployments/${encodeURIComponent(deploymentId)}/simulate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  }

  async getReadiness(): Promise<ReadinessResponse> {
    return this.request('/readiness');
  }

  async getFleetAnchors(): Promise<FleetAnchorsResponse> {
    return this.request('/fleets');
  }

  async getDevboxSnapshot(url: string): Promise<DeploymentSnapshot> {
    return this.request(`/devbox?url=${encodeURIComponent(url)}`);
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await this.fetchApi.fetch(`${await this.baseUrl()}${path}`, {
      ...init,
    });
    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
    return res.json() as Promise<T>;
  }
}
